import { inject, Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { first, map, Observable, of } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { CanvasElement } from '@blaze-runtime/engine-domain';
import { WizardDraft, WizardFormDraft } from '@blaze-shared/elements-domain';

import { findParentFormGroupLikeOfForm } from '../../../../../utils/layout/relationship';
import { isWizard } from '../../../../element-model/element-type-guards';

@Injectable({ providedIn: 'root' })
export class WizardDraftService {
  private readonly store = inject(Store);

  getWizardFormDraft(
    form: CanvasElement | undefined,
    formSelector: (element: CanvasElement) => boolean,
    elements: Dictionary<CanvasElement>,
  ): Observable<WizardFormDraft | undefined> {
    if (!form || !formSelector(form)) {
      return of(undefined);
    }

    const wizard = findParentFormGroupLikeOfForm(elements, form.id);
    if (!isWizard(wizard)) {
      throw new Error('wizard parent not found');
    }

    return this.getWizardDraft(wizard).pipe(map((draft) => draft?.[form.id]));
  }

  getWizardDraft(wizard: CanvasElement | undefined): Observable<WizardDraft | undefined> {
    if (!isWizard(wizard)) {
      return of(undefined);
    }

    if (wizard.props.content.action !== 'add') {
      return of(undefined);
    }

    // raw store props (no defaults merged), so absence of the flag means enabled
    if (wizard.props.content.saveDraftEnabled === false) {
      return of(undefined);
    }

    return this.store.select(runtimeSelectors.selectElementMetaDataFeaturesById(wizard.id)).pipe(
      map((elementMeta) =>
        elementMeta && elementMeta.elementType === 'wizard' ? elementMeta.draft : undefined,
      ),
      first(Boolean),
    );
  }
}
